import { Component } from "@theme/component";
import { yieldToMainThread } from "@theme/utilities";

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element | null} */
  source = null;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element | null} */
  destination = null;

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    // Define bezier curve points
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    // Position the flying thingy back to the start point
    if (this.useSourceSize) {
      this.style.setProperty("--width", `${sourceRect.width}px`);
      this.style.setProperty("--height", `${sourceRect.height}px`);
    }
    this.style.setProperty("--start-x", `${startPoint.x}px`);
    this.style.setProperty("--start-y", `${startPoint.y}px`);
    this.style.setProperty("--travel-x", `${endPoint.x - startPoint.x}px`);
    this.style.setProperty("--travel-y", `${endPoint.y - startPoint.y}px`);

    await yieldToMainThread();

    await Promise.allSettled(this.getAnimations().map(animation => animation.finished));
    // Notify listeners (cart-icon, etc.) that the capsule has visually arrived
    // at the cart icon. Bubbles up so document-level listeners can synchronize
    // their feedback (cart bubble pop) with the capsule's visual landing rather
    // than with the network-driven cartUpdate event, which fires on its own
    // clock and can race ahead of (fast network) or lag behind (slow network)
    // the capsule's animation.
    this.dispatchEvent(new CustomEvent("fly-to-cart:landed", { bubbles: true }));
    this.remove();
  };

  connectedCallback() {
    super.connectedCallback();

    // Read the source/destination geometry SYNCHRONOUSLY, before the browser
    // gets a chance to paint this element. The CSS animation auto-starts the
    // instant the element connects (animation-name lives on the base
    // `fly-to-cart` rule), so the --start-x/--start-y custom properties must be
    // applied in the same synchronous turn as connection. An asynchronous geometry
    // read would first paint the element at its CSS-default position in the viewport's
    // top-left corner. A direct getBoundingClientRect prevents that stray frame.
    if (!this.source || !this.destination) {
      this.remove();
      return;
    }
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    // Nothing to fly between if either anchor has no layout box (detached,
    // display:none, etc.) -- bail without leaving a stranded element.
    if (!sourceRect.width || !destinationRect.width) {
      this.remove();
      return;
    }

    this.#animate(sourceRect, destinationRect).catch(() => {
      this.remove();
    });
  }
}

if (!customElements.get("fly-to-cart")) {
  customElements.define("fly-to-cart", FlyToCart);
}
